import React, { useState } from 'react';
import { FileText, Check, X, ZoomIn, Edit2, ArrowRight, AlertTriangle, Eye, Download, ArrowLeft, Mail } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
import { Page } from '../components/Layout';
const CLAIM_PDF = 'https://cdn.magicpatterns.com/uploads/7ZdRftQEiAEJFYNVwrreEE/image.png';
const BOL_PDF = 'https://cdn.magicpatterns.com/uploads/69cK22deVhYzwNxexZYGh1/image.png';
function PdfModal({
  src,
  title,
  onClose
}: {
  src: string;
  title: string;
  onClose: () => void;
}) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-6" onClick={onClose} data-id="element-464">
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-465">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 flex-shrink-0" data-id="element-466">
          <div className="flex items-center gap-2" data-id="element-467">
            <FileText className="w-4 h-4 text-blue-600" data-id="element-468" />
            <span className="text-sm font-semibold text-slate-800" data-id="element-469">
              {title}
            </span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors" data-id="element-470">
            <X className="w-4 h-4" data-id="element-471" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4" data-id="element-472">
          <img src={src} alt={title} className="w-full rounded-lg" data-id="element-473" />
        </div>
      </div>
    </div>;
}
export function InvestigationWorkbench({
  onNavigate,
  onBackToCheck
}: {
  onNavigate?: (page: Page) => void;
  onBackToCheck?: () => void;
}) {
  const [activeTab, setActiveTab] = useState('shortage');
  const [claimPdfOpen, setClaimPdfOpen] = useState(false);
  const [bolPdfOpen, setBolPdfOpen] = useState(false);
  const handleBack = () => {
    if (onBackToCheck) onBackToCheck();
    if (onNavigate) onNavigate('checks');
  };
  return <div className="space-y-6 h-full flex flex-col" data-id="element-474">
      {claimPdfOpen && <PdfModal src={CLAIM_PDF} title="Claim Document — Dollar General Chargeback" onClose={() => setClaimPdfOpen(false)} data-id="element-475" />}
      {bolPdfOpen && <PdfModal src={BOL_PDF} title="Bill of Lading" onClose={() => setBolPdfOpen(false)} data-id="element-476" />}

      <div data-id="element-477">
        <button onClick={handleBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-4" data-id="element-478">
          <ArrowLeft className="w-4 h-4" data-id="element-479" />
          Back to Check #DG-8821
        </button>

        <div className="flex items-center justify-between" data-id="element-480">
          <div data-id="element-481">
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight" data-id="element-482">
              AI Audit & Review
            </h2>
            <p className="text-slate-500 mt-1" data-id="element-483">
              Review AI findings and approve disputes.
            </p>
          </div>
          <div className="flex space-x-3" data-id="element-484">
            <div className="px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-semibold border border-emerald-200 flex items-center space-x-2" data-id="element-485">
              <Check className="w-4 h-4" data-id="element-486" />
              <span data-id="element-487">AI Auto Reviewed</span>
            </div>
          </div>
        </div>
      </div>

      {/* Case Header */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex items-center justify-between" data-id="element-488">
        <div className="flex items-center space-x-6" data-id="element-489">
          <div data-id="element-490">
            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-491">
              Retailer
            </span>
            <div className="font-bold text-slate-900" data-id="element-492">
              <span data-id="element-493">Dollar General</span>
            </div>
          </div>
          <div className="h-8 w-px bg-slate-200" data-id="element-494"></div>
          <div data-id="element-495">
            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-496">
              Deduction ID
            </span>
            <div className="font-medium text-slate-900" data-id="element-497">PO-88201</div>
          </div>
          <div className="h-8 w-px bg-slate-200" data-id="element-498"></div>
          <div data-id="element-499">
            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-500">
              Type
            </span>
            <div className="font-medium text-slate-900" data-id="element-501">Shortage</div>
          </div>
          <div className="h-8 w-px bg-slate-200" data-id="element-502"></div>
          <div data-id="element-503">
            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-504">
              Amount
            </span>
            <div className="font-bold text-rose-600" data-id="element-505">-$600</div>
          </div>
          <div className="h-8 w-px bg-slate-200" data-id="element-506"></div>
          <div data-id="element-507">
            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-508">
              Invoice Number
            </span>
            <div className="font-medium text-slate-900" data-id="element-509">INV-2024-001</div>
          </div>
        </div>
      </div>

      {/* 3-Way Match View */}
      <div className="flex-1 grid grid-cols-3 gap-6 min-h-0" data-id="element-510">
        {/* Column 1: The Claim (Retailer) */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden" data-id="element-511">
          <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center" data-id="element-512">
            <div className="flex items-center space-x-2" data-id="element-513">
              <FileText className="w-4 h-4 text-slate-500" data-id="element-514" />
              <h3 className="font-bold text-slate-700" data-id="element-515">The Claim (Retailer)</h3>
            </div>
            <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded" data-id="element-516">
              Source: Deduction PDF
            </span>
          </div>

          <div className="p-6 space-y-6 flex-1 overflow-y-auto custom-scrollbar" data-id="element-517">
            <div className="space-y-1" data-id="element-518">
              <label className="text-xs font-semibold text-slate-500 uppercase" data-id="element-519">
                Claim Code
              </label>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-slate-900 font-medium" data-id="element-520">
                Shortage (Code 14)
              </div>
            </div>

            <div className="space-y-1" data-id="element-521">
              <label className="text-xs font-semibold text-slate-500 uppercase" data-id="element-522">
                Item / SKU
              </label>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-slate-900" data-id="element-523">
                <div className="font-medium" data-id="element-524">Oral-B Glide Floss</div>
                <div className="text-xs text-slate-500" data-id="element-525">SKU: 123-456-789</div>
              </div>
            </div>

            <div className="space-y-1" data-id="element-526">
              <label className="text-xs font-semibold text-slate-500 uppercase" data-id="element-527">
                Qty Claimed (Received)
              </label>
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-amber-700 font-bold text-lg flex justify-between items-center" data-id="element-528">
                <span data-id="element-529">95 Cases</span>
                <AlertTriangle className="w-5 h-5 text-amber-500" data-id="element-530" />
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-slate-100" data-id="element-531">
              <div className="flex items-center justify-between mb-2" data-id="element-532">
                <label className="text-xs font-semibold text-slate-500 uppercase" data-id="element-533">
                  Claim Document
                </label>
                <button onClick={() => setClaimPdfOpen(true)} className="text-xs text-blue-600 hover:underline flex items-center" data-id="element-534">
                  <Eye className="w-3 h-3 mr-1" data-id="element-535" /> View Full PDF
                </button>
              </div>
              <div onClick={() => setClaimPdfOpen(true)} className="rounded-lg border border-slate-200 overflow-hidden relative group cursor-pointer" data-id="element-536">
                <img src={CLAIM_PDF} alt="Claim PDF Snippet" className="w-full object-cover object-top" style={{
                maxHeight: '160px'
              }} data-id="element-537" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center" data-id="element-538">
                  <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" data-id="element-539" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Column 2: Internal Records */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden" data-id="element-540">
          <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center" data-id="element-541">
            <div className="flex items-center space-x-2" data-id="element-542">
              <FileText className="w-4 h-4 text-slate-500" data-id="element-543" />
              <h3 className="font-bold text-slate-700" data-id="element-544">Internal Records</h3>
            </div>
            <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded" data-id="element-545">
              Source: ERP + BOL
            </span>
          </div>

          <div className="p-6 space-y-6 flex-1 overflow-y-auto custom-scrollbar" data-id="element-546">
            <div className="space-y-1" data-id="element-547">
              <label className="text-xs font-semibold text-slate-500 uppercase" data-id="element-548">
                Carrier
              </label>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-slate-900 font-medium" data-id="element-549">
                Swift Carriers
              </div>
            </div>

            <div className="space-y-1" data-id="element-550">
              <label className="text-xs font-semibold text-slate-500 uppercase" data-id="element-551">
                Item / SKU
              </label>
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-slate-900" data-id="element-552">
                <div className="font-medium" data-id="element-553">Oral-B Glide Floss</div>
                <div className="text-xs text-slate-500" data-id="element-554">SKU: 123-456-789</div>
              </div>
            </div>

            <div className="space-y-1" data-id="element-555">
              <label className="text-xs font-semibold text-slate-500 uppercase" data-id="element-556">
                Invoiced Qty
              </label>
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-amber-700 font-bold text-lg flex justify-between items-center" data-id="element-557">
                <span data-id="element-558">100 Cases</span>
                <AlertTriangle className="w-5 h-5 text-amber-500" data-id="element-559" />
              </div>
            </div>

            <div className="space-y-1" data-id="element-560">
              <div className="flex justify-between items-center mb-2" data-id="element-561">
                <label className="text-xs font-semibold text-slate-500 uppercase" data-id="element-562">
                  BOL Document
                </label>
                <button onClick={() => setBolPdfOpen(true)} className="text-xs text-blue-600 hover:underline flex items-center" data-id="element-563">
                  <Eye className="w-3 h-3 mr-1" data-id="element-564" /> View Full PDF
                </button>
              </div>
              <div onClick={() => setBolPdfOpen(true)} className="rounded-lg border border-slate-200 overflow-hidden relative group cursor-pointer" data-id="element-565">
                <img src={BOL_PDF} alt="BOL Document" className="w-full object-cover object-top" style={{
                maxHeight: '160px'
              }} data-id="element-566" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center" data-id="element-567">
                  <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" data-id="element-568" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Column 3: The Verdict (AI) */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col overflow-hidden ring-1 ring-blue-500/20" data-id="element-569">
          <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center" data-id="element-570">
            <div className="flex items-center space-x-2" data-id="element-571">
              <Check className="w-4 h-4 text-slate-500" data-id="element-572" />
              <h3 className="font-bold text-slate-700" data-id="element-573">AI Verdict</h3>
            </div>
            <div className="flex items-center gap-2" data-id="element-574">
              <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded" data-id="element-575">
                98% Match
              </span>
            </div>
          </div>

          <div className="p-6 space-y-6 flex-1 overflow-y-auto custom-scrollbar" data-id="element-576">
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-577">
              <div className="flex items-center space-x-2 mb-2" data-id="element-578">
                <AlertTriangle className="w-5 h-5 text-amber-500" data-id="element-579" />
                <span className="font-bold text-amber-700" data-id="element-580">
                  Mismatch detected with high confidence
                </span>
              </div>
              <p className="text-sm text-amber-800" data-id="element-581">
                Retailer claims 95 cases received, but BOL shows driver signed
                for 100 cases. Shortage occurred in transit (FOB Origin).
              </p>
            </div>

            <div className="space-y-3" data-id="element-582">
              <h4 className="text-sm font-semibold text-slate-900" data-id="element-583">
                Recommended Action
              </h4>
              <div className="flex flex-col space-y-2" data-id="element-584">
                <button onClick={() => onNavigate && onNavigate('outbox')} className="w-full p-3 bg-white border border-slate-200 rounded-lg flex items-center justify-between group hover:bg-slate-50 transition-colors text-left" data-id="element-585">
                  <div className="flex items-center gap-2" data-id="element-586">
                    <Mail className="w-4 h-4 text-slate-400" data-id="element-587" />
                    <span className="font-medium text-slate-700" data-id="element-588">
                      View Email to Retailer
                    </span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:translate-x-0.5 transition-transform" data-id="element-589" />
                </button>
              </div>
            </div>

            <div className="pt-6 border-t border-slate-100" data-id="element-590">
              <h4 className="text-sm font-semibold text-slate-900 mb-3" data-id="element-591">
                Draft Email Preview
              </h4>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 text-sm text-slate-600 font-mono text-xs leading-relaxed" data-id="element-592">
                <p className="mb-2" data-id="element-593">
                  <span className="font-bold text-slate-900" data-id="element-594">Subject:</span>{' '}
                  Dispute - PO# 12345 - Shortage Claim
                </p>
                <p data-id="element-595">Dear AP Team,</p>
                <p className="mt-2" data-id="element-596">Regarding Deduction #999 for PO #12345.</p>
                <p className="mt-2" data-id="element-597">
                  We invoiced 100 units. Your carrier (Swift Transport) signed
                  for 100 units on the attached BOL (See Page 1).
                </p>
                <p className="mt-2" data-id="element-598">
                  Please repay the shortage of 5 units ($500).
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}